<template>
  <div class="team-list-skeleton" aria-busy="true" aria-live="polite">
    <span class="sr-only">{{ $t('general.loading_team') }}</span>

    <div class="team-list-skeleton__table team-table-card">
      <div class="team-table-scroll">
        <table class="team-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('general.employee') }}</th>
              <th scope="col">{{ $t('general.region') }}</th>
              <th scope="col">{{ $t('general.department') }}</th>
              <th scope="col">{{ $t('general.branch') }}</th>
              <th scope="col">{{ $t('general.position') }}</th>
              <th scope="col">{{ $t('general.job_title') }}</th>
              <th class="team-table__count" scope="col">{{ $t('general.total_competencies') }}</th>
              <th class="team-table__action" scope="col">{{ $t('general.action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in count" :key="`table-skeleton-${n}`">
              <td>
                <div class="team-person">
                  <span class="shimmer skeleton-avatar"></span>
                  <span class="shimmer skeleton-name"></span>
                </div>
              </td>
              <td><span class="shimmer skeleton-cell"></span></td>
              <td><span class="shimmer skeleton-cell"></span></td>
              <td><span class="shimmer skeleton-cell"></span></td>
              <td><span class="shimmer skeleton-cell"></span></td>
              <td><span class="shimmer skeleton-cell"></span></td>
              <td class="team-table__count">
                <span class="shimmer skeleton-count"></span>
              </td>
              <td class="team-table__action">
                <span class="shimmer skeleton-action"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="team-list-skeleton__cards">
      <div
        v-for="n in count"
        :key="`card-skeleton-${n}`"
        class="team-row-skeleton"
        aria-hidden="true"
      >
        <div class="team-row-skeleton__top">
          <span class="shimmer skeleton-detail"></span>
          <span class="shimmer skeleton-avatar"></span>
          <div class="team-row-skeleton__info">
            <span class="shimmer skeleton-name"></span>
            <span class="shimmer skeleton-meta"></span>
            <span class="shimmer skeleton-meta skeleton-meta--count"></span>
          </div>
        </div>
        <div class="team-row-skeleton__actions">
          <span class="shimmer skeleton-action"></span>
          <span class="shimmer skeleton-action skeleton-action--edu"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TeamListSkeleton",
  props: {
    count: {
      type: Number,
      default: 6,
    },
  },
};
</script>

<style scoped>
.team-list-skeleton__cards {
  display: none;
}

.team-table-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(30, 41, 59, 0.04);
}

.team-table-scroll {
  width: 100%;
  overflow-x: auto;
}

.team-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  font-size: 13px;
}

.team-table th,
.team-table td {
  padding: 14px 13px;
  border-bottom: 1px solid #edf1f6;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
}

.team-table th {
  color: #64748b;
  background: #f8fafc;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.team-table tbody tr:last-child td {
  border-bottom: 0;
}

.team-person {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
}

.team-table__count {
  text-align: center !important;
}

.team-table__action {
  text-align: right !important;
}

.shimmer {
  display: inline-block;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: team-skeleton-loading 1.5s infinite;
  border-radius: 6px;
}

.skeleton-avatar {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
}

.skeleton-name {
  width: 120px;
  height: 14px;
}

.skeleton-cell {
  width: 72px;
  height: 12px;
}

.skeleton-count {
  width: 28px;
  height: 24px;
  border-radius: 999px;
}

.skeleton-action {
  width: 180px;
  height: 34px;
  border-radius: 8px;
}

.skeleton-meta {
  display: block;
  width: 55%;
  max-width: 140px;
  height: 10px;
  margin-top: 6px;
}

.skeleton-edu {
  width: 18px;
  height: 18px;
  border-radius: 4px;
}

.skeleton-detail {
  width: 40px;
  height: 40px;
  border-radius: 10px;
}

.team-row-skeleton__top,
.team-row-skeleton__actions {
  display: none;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes team-skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 800px) {
  .team-list-skeleton__table {
    display: none;
  }

  .team-list-skeleton__cards {
    display: block;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e8edf5;
    border-radius: 12px;
  }

  .team-row-skeleton {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid #eef2f7;
  }

  .team-row-skeleton:last-child {
    border-bottom: 0;
  }

  .team-row-skeleton__top,
  .team-row-skeleton__actions {
    display: flex;
    min-width: 0;
  }

  .team-row-skeleton__top {
    align-items: center;
    gap: 8px;
  }

  .team-row-skeleton__info {
    flex: 1 1 auto;
    min-width: 0;
  }

  .team-row-skeleton__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .skeleton-detail {
    flex: 0 0 40px;
  }

  .skeleton-avatar {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
  }

  .skeleton-name {
    display: block;
    width: 48%;
    max-width: 130px;
    height: 12px;
  }

  .skeleton-meta {
    width: 62%;
    max-width: 160px;
  }

  .skeleton-meta--count {
    width: 42%;
    max-width: 110px;
  }

  .skeleton-action {
    display: block;
    width: 100%;
    height: 36px;
    border-radius: 8px;
  }
}

@media (max-width: 639px) {
  .team-list-skeleton__cards {
    width: 100%;
    min-width: 0;
    border-radius: 12px;
  }

  .team-row-skeleton {
    min-height: 132px;
    box-sizing: border-box;
    padding: 12px;
  }

  .skeleton-detail {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
  }

  .skeleton-action {
    height: 48px;
    min-height: 48px;
    border-radius: 10px;
  }
}
</style>
